import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Scale, Mail, Lock, ArrowRight, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationNeeded, setVerificationNeeded] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendingEmail, setResendingEmail] = useState(false);
  const { updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next') || '/discover';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/login', { email, password });
      
      // Check if verification is required
      if (response.data.requires_verification) {
        setVerificationNeeded(true);
        setVerificationEmail(response.data.email);
        if (response.data.email_sent) {
          toast.info('Verification email sent! Please check your inbox.');
        } else {
          toast.info('Please verify your email first');
        }
        return;
      }
      
      // Normal login
      localStorage.setItem('fundfair_token', response.data.access_token);
      updateUser(response.data.user);
      toast.success('Welcome back!');
      navigate(nextPath);
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid email or password';
      if (message.toLowerCase().includes('no account found') || message.toLowerCase().includes('register')) {
        toast.error(
          <div>
            {message}{' '}
            <a href="/signup" className="underline font-medium">Click here to sign up</a>
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingEmail(true);
    try {
      const response = await api.post('/auth/resend-verification', { email: verificationEmail });
      if (response.data.email_sent) {
        toast.success('Verification email sent! Check your inbox.');
      } else {
        toast.info('Verification email will be sent shortly.');
      }
    } catch (error) {
      toast.error('Failed to resend verification email');
    } finally {
      setResendingEmail(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-background to-secondary/50 flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
            <Scale className="w-6 h-6 text-primary-foreground" />
          </div>
          <span className="text-2xl font-bold text-foreground tracking-tight">
            Fund<span className="text-primary">fair</span>
          </span>
        </div>

        <Card className="border-border/50 shadow-xl">
          <CardHeader className="space-y-1 text-center pb-4">
            <CardTitle className="text-2xl">
              {verificationNeeded ? 'Verify Your Email' : 'Welcome back'}
            </CardTitle>
            <CardDescription>
              {verificationNeeded 
                ? 'Please check your email to verify your account'
                : 'Sign in to continue making a difference'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {verificationNeeded ? (
              <div className="space-y-4">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-center">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-yellow-600" />
                  </div>
                  <h3 className="font-semibold text-yellow-800 mb-2">Check Your Email!</h3>
                  <p className="text-sm text-yellow-700 mb-2">
                    We sent a verification link to <strong>{verificationEmail}</strong>.
                  </p>
                  <p className="text-xs text-yellow-600">
                    Click the link in the email to verify your account, then try logging in again.
                  </p>
                </div>
                
                <Button
                  variant="outline"
                  className="w-full rounded-xl"
                  onClick={handleResendVerification}
                  disabled={resendingEmail}
                >
                  {resendingEmail ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Resend Verification Email'
                  )}
                </Button>

                <Button
                  variant="ghost"
                  className="w-full rounded-xl"
                  onClick={() => {
                    setVerificationNeeded(false);
                    setVerificationEmail('');
                  }}
                >
                  Back to Login
                </Button>
              </div>
            ) : (
              <>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 h-12 rounded-xl"
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      <Link
                        to="/forgot-password"
                        className="text-sm text-primary hover:underline"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 h-12 rounded-xl"
                        data-testid="login-password-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 rounded-xl text-base font-semibold btn-bounce"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        Sign In
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    Do not have an account?{' '}
                    <Link
                      to={nextPath !== '/discover' ? `/signup?next=${encodeURIComponent(nextPath)}` : '/signup'}
                      className="text-primary font-medium hover:underline"
                      data-testid="signup-link"
                    >
                      Create one
                    </Link>
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Collect funds. Upload receipts. Repeat.
        </p>
      </div>
    </div>
  );
}
